import { FC } from "react";
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Modal } from "components/Modal";
import { ReportAlert } from "pages/UploadCSV/UploadCSVUsers/components/ReportAlert";
import { useSyncCognitoAttributes } from "./useSyncCognitoAttributes";

type SyncCognitoAttributesModalProps = {
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void;
};

export const SyncCognitoAttributesModal: FC<SyncCognitoAttributesModalProps> = ({
  open,
  onClose,
  onCompleted,
}) => {
  const {
    loading,
    syncing,
    completed,
    users,
    error,
    loadingData,
    report,
    syncUsers,
    reset,
  } = useSyncCognitoAttributes(open);

  const handleClose = () => {
    if (completed) {
      onCompleted?.();
    }
    reset();
    onClose();
  };

  const progressPercent =
    loadingData.total > 0
      ? Math.floor((loadingData.current / loadingData.total) * 100)
      : 0;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Sync Cognito Custom Attributes"
      size="large"
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          These Cognito users are missing `custom:id` or `custom:role`, or the
          values do not match the database. They will be updated one by one.
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : !completed && users.length === 0 ? (
          <Typography>
            All Cognito users already have the correct custom attributes.
          </Typography>
        ) : !completed ? (
          <>
            <Typography variant="body2">
              {users.length} user{users.length === 1 ? "" : "s"} will be synced.
            </Typography>
            <TableContainer sx={{ maxHeight: 360 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.user_id}>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell sx={{ textTransform: "capitalize" }}>
                        {user.role}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : null}

        {syncing && (
          <Box py={2}>
            <Typography variant="body1" align="center">
              Syncing attributes {progressPercent}%
            </Typography>
            <LinearProgress variant="determinate" value={progressPercent} />
          </Box>
        )}

        {completed && (
          <Stack spacing={2}>
            {report.success.length > 0 && (
              <ReportAlert
                report={report.success}
                severity="success"
                textDescription="users updated in Cognito"
              />
            )}
            {report.skipped.length > 0 && (
              <ReportAlert
                report={report.skipped}
                severity="info"
                textDescription="users were already up to date"
              />
            )}
            {report.error.length > 0 && (
              <ReportAlert
                report={report.error}
                severity="error"
                textDescription="users could not be synced"
              />
            )}
          </Stack>
        )}

        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          {!completed ? (
            <>
              <Button variant="outlined" onClick={handleClose} disabled={syncing}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={syncUsers}
                disabled={loading || syncing || users.length === 0 || !!error}
              >
                {syncing ? "Syncing..." : "Confirm and Sync"}
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={handleClose}>
              Complete
            </Button>
          )}
        </Stack>
      </Stack>
    </Modal>
  );
};
